import classNames from "classnames"
import {
    ColorSwatchCategory,
    formattedDistanceUnit,
    hexColorSwatches,
    LineWidth,
    lineWidthNameByLineWidth,
    zIndex,
} from "@nova/constants"
import * as Icons from "@nova/component-icons"
import {
    SecondaryFlyoutMenu,
    SecondaryFlyoutMenuContentItem,
} from "../../icons/TODO-put-in-package/SecondaryFlyoutMenu"
import { useEffect, useMemo, useRef, useState } from "react"
import { useFirePerimeterBuffers } from "./useFirePerimeterBuffers"
import { InspectorPanelAccordion } from "../../accordion/InspectorPanelAccordion"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { InspectorPanelKeyValueDropdown } from "../../InspectorPanelKeyValueDropdown"
import { thicknesses } from "../../lines/constants"
import { LineThicknessPicker } from "../../lines/LineThicknessPicker"
import { ButtonSecondary, ButtonStatus } from "@nova/component-btn"
import { perimeterDistanceOptions } from "../../hotspots/constants"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectIdRenaming,
    setIdRenaming,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { ColorPalette } from "../../icons/TODO-put-in-package/ColorPalette"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import { selectFirePerimeterById } from "features/entity-caching/fire-perimeters/selectors"

const firePerimeterOutlineColorSwatches = hexColorSwatches[
    ColorSwatchCategory.FirePerimeterBuffer
].map((hex) => encodeColorToNumber(hex))

type InspectorPanelFirePerimeterLayerBuffersProps = {
    // These refer to the single dropdown instance that can be open in the inspector panel.
    openDropdownId?: string
    setOpenDropdownId: (id: string | undefined) => void

    // These refer to the set of accordion categories that are open.
    openCategories: Set<string>
    setCategoryIsOpen: (id: string, isOpen: boolean) => void
}

export const InspectorPanelFirePerimeterLayerBuffers = ({
    openDropdownId,
    setOpenDropdownId,
    openCategories,
    setCategoryIsOpen,
    id,
}: InspectorPanelFirePerimeterLayerBuffersProps & { id: string }) => {
    const perimeter = useAppSelector(selectFirePerimeterById(id))

    const {
        distanceUnit,
        unitAbbreviation,
        buffers,
        createBuffer,
        editBufferAtIndex,
        deleteBufferAtIndex,
        updateBuffersArray,
        isGeneratingBuffer,
        bufferDistancesBeingUpdated,
    } = useFirePerimeterBuffers(perimeter)

    const flyoutContentItems: SecondaryFlyoutMenuContentItem[] = useMemo(
        () =>
            perimeterDistanceOptions.map((option) => ({
                id: option,
                title: formattedDistanceUnit[option].full,
                onClick: () => updateBuffersArray({ unit: option }),
                isSelected: option === distanceUnit,
            })),
        [updateBuffersArray, distanceUnit]
    )

    return (
        <>
            <div className="flex items-center justify-between mx-4 pt-2 pb-1 border-t border-gray-700">
                <h5 className="text-gray-0 select-none">Buffer</h5>

                {/* Unit Dropdown */}
                <div className="relative z-10">
                    {/* Dropdown Button */}
                    <div
                        className={classNames(
                            "rounded-md border hover:border-gray-500 flex items-center justify-center text-gray-0 pl-2 pr-0.5 group cursor-pointer",
                            {
                                "border-transparent": openDropdownId !== "unit",
                                "border-gray-500 bg-gray-500": openDropdownId === "unit",
                            }
                        )}
                        onClick={() => {
                            setOpenDropdownId(openDropdownId === "unit" ? undefined : "unit")
                        }}
                    >
                        <small className="select-none">
                            {formattedDistanceUnit[distanceUnit].abbrev}
                        </small>
                        <Icons.SmallIcon
                            Icon={Icons.ArrowDropdown}
                            className={classNames("group-hover:text-gray-0", {
                                "text-gray-300": openDropdownId !== "unit",
                                "text-gray-0": openDropdownId === "unit",
                            })}
                        />
                    </div>

                    {/* Actual Dropdown */}
                    {openDropdownId === "unit" && (
                        <div className="absolute right-0" style={{ top: "calc(100% + 0.25rem)" }}>
                            <SecondaryFlyoutMenu
                                className="whitespace-nowrap"
                                items={flyoutContentItems}
                                onBlur={() => {
                                    setOpenDropdownId(undefined)
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
            <>
                {/* Each Buffer Accordion */}
                {buffers?.map((buffer, i) => (
                    <Buffer
                        key={`buffer-${buffer.distance}-${i}`}
                        index={i}
                        buffer={buffer}
                        unitAbbreviation={unitAbbreviation}
                        updateBuffer={(updates) => editBufferAtIndex(i, updates)}
                        deleteBuffer={() => deleteBufferAtIndex(i)}
                        openDropdownId={openDropdownId}
                        setOpenDropdownId={setOpenDropdownId}
                        openCategories={openCategories}
                        setCategoryIsOpen={setCategoryIsOpen}
                        distanceIsBeingUpdated={bufferDistancesBeingUpdated.has(buffer.distance)}
                        canEditDistance={
                            bufferDistancesBeingUpdated.size === 0 && !isGeneratingBuffer
                        }
                    />
                ))}
            </>

            {/* Add Buffer Line Button */}
            {(!buffers || buffers.length < 5) && (
                <div className="mx-4">
                    <ButtonSecondary
                        title="Add Buffer Line"
                        LeftIcon={Icons.Plus}
                        className="w-full"
                        status={isGeneratingBuffer ? ButtonStatus.Loading : undefined}
                        disabled={isGeneratingBuffer || bufferDistancesBeingUpdated.size > 0}
                        onClick={() => createBuffer()}
                    />
                </div>
            )}
        </>
    )
}

const Buffer = ({
    buffer,
    index,
    unitAbbreviation,
    updateBuffer,
    deleteBuffer,
    openDropdownId,
    setOpenDropdownId,
    openCategories,
    setCategoryIsOpen,
    distanceIsBeingUpdated,
    canEditDistance,
}: {
    buffer: {
        width: LineWidth
        distance: number
        color: number
    }
    index: number
    unitAbbreviation: string
    updateBuffer: (updates: { buffer?: number; color?: number; width?: LineWidth }) => void
    deleteBuffer: () => void
    distanceIsBeingUpdated: boolean
    canEditDistance: boolean
} & InspectorPanelFirePerimeterLayerBuffersProps) => {
    const id = `buffer-${buffer.distance}-${index}`
    const isOpen = openCategories.has(id)
    const colorPaletteIsOpen = openDropdownId === id + "-color"
    const loadingText = useLoadingText(distanceIsBeingUpdated, "Updating")

    const dispatch = useAppDispatch()

    const isRenaming = useAppSelector(selectIdRenaming()) === id
    const ref = useRef<HTMLInputElement>(undefined)

    const [resizeTrigger, triggerResize] = useState(0)

    useEffectUnsafe(() => {
        if (!isOpen && colorPaletteIsOpen) setOpenDropdownId(undefined)
    }, [isOpen])

    useEffect(() => {
        triggerResize((prev) => ++prev)
    }, [colorPaletteIsOpen])

    useEffect(() => {
        if (isRenaming) ref.current?.focus()
    }, [isRenaming])

    return (
        <InspectorPanelAccordion
            key={id}
            extraResizeDependency={resizeTrigger}
            // This ensures that the accordion resizes accordingly, depending on whether the color palette is open.
            headerExtraHeightCalculationInRem={colorPaletteIsOpen ? 2.375 : 0}
            customHeaderComponent={
                <div className="flex gap-2.5 items-center group w-full">
                    {/* Color Swatch */}
                    <div className="relative">
                        <div
                            className="w-5 h-5 rounded-md flex-shrink-0"
                            style={{
                                backgroundColor: decodeNumberToColor(buffer.color),
                            }}
                            onClick={(e) => {
                                e.stopPropagation()
                                const colorId = id + "-color"
                                if (openDropdownId !== colorId) setOpenDropdownId(colorId)
                                else setOpenDropdownId(undefined)

                                if (!isOpen) setCategoryIsOpen(id, true)
                            }}
                        />

                        {/* Dropdown Itself */}
                        {openCategories.has(id) && colorPaletteIsOpen && (
                            <div
                                className="absolute"
                                style={{
                                    zIndex: zIndex.InspectorPanel + 50,
                                    top: "calc(100% + 0.125rem)",
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ClickAwayListener onClickAway={() => setOpenDropdownId(undefined)}>
                                    <ColorPalette
                                        swatches={firePerimeterOutlineColorSwatches}
                                        selectedColor={buffer.color}
                                        setSelectedColor={(color) => updateBuffer({ color })}
                                        setOpenDropdownId={setOpenDropdownId}
                                        triggerResize={() =>
                                            setTimeout(() => triggerResize((prev) => ++prev), 0)
                                        }
                                    />
                                </ClickAwayListener>
                            </div>
                        )}
                    </div>

                    {distanceIsBeingUpdated ? (
                        <>
                            <div className="flex items-center gap-1">
                                <NovaSpinner width={20} height={20} />
                                <h5 className="text-gray-100">{loadingText}</h5>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Renamable Distance */}
                            <RenamableText
                                ref={ref}
                                Tag="h5"
                                title={`${isRenaming ? buffer.distance : Math.round(buffer.distance)}${!isRenaming ? ` ${unitAbbreviation}` : ""}`}
                                id={id}
                                isRenaming={isRenaming}
                                className={classNames("mono cursor-text flex-shrink-1", {
                                    "text-gray-100 hover:text-gray-0": !isRenaming,
                                    "text-gray-0": isRenaming,
                                })}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (canEditDistance) dispatch(setIdRenaming(id))
                                }}
                                onCancelRenaming={() => dispatch(setIdRenaming(undefined))}
                                onConfirmRenaming={(_, value) => {
                                    if (!canEditDistance) return
                                    updateBuffer({ buffer: Number(value) })
                                    dispatch(setIdRenaming(undefined))
                                }}
                                sanitizer={(value) =>
                                    value
                                        .replace(/[^\d.-]/g, "")
                                        .replace(/(?!^)-/g, "")
                                        .replace(/^(-?\d*)\.(?=.*\.)/, "$1")
                                }
                                isFullWidth={false}
                            />

                            {/* Delete Buffer Button */}
                            <div className="flex-grow invisible group-hover:visible" />
                            <Icons.SmallIcon
                                Icon={Icons.Minus}
                                className="text-gray-100 hover:text-gray-0 invisible group-hover:visible flex-shrink-0"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    deleteBuffer()
                                }}
                            />
                        </>
                    )}
                </div>
            }
            isOpen={isOpen}
            setIsOpen={(isOpen) => setCategoryIsOpen(id, isOpen)}
            headerVerticalPaddingInRem={0.25}
        >
            {/* Thickness Dropdown */}
            <InspectorPanelKeyValueDropdown
                id={`${id}-thickness`}
                isOpen={openDropdownId === `${id}-thickness`}
                setOpenDropdownId={setOpenDropdownId}
                keyTitle="Thickness"
                isInSubSection={true}
                Icon={thicknesses[buffer.width]}
                dropdownTitle={lineWidthNameByLineWidth[buffer.width]}
                iconClasses="text-gray-0"
            >
                <LineThicknessPicker
                    selectedThickness={buffer.width}
                    setSelectedThickness={(width) => updateBuffer({ width })}
                    setOpenDropdownId={setOpenDropdownId}
                />
            </InspectorPanelKeyValueDropdown>
        </InspectorPanelAccordion>
    )
}
